import type { FC, ReactNode } from 'react';

import * as styles from './mapping-cell.css';
import { TypeChip } from './type-chip';

/** The cell spaces its two lines itself. */
const NO_CHIP_GAP = { marginLeft: 0 };

interface MappingCellProps {
  /** The field's name (a column, a workflow input, a response field). */
  name: ReactNode;
  /** The field's type; without one the second line shows `note`. */
  type?: string;
  /** Where the field comes from, before its name (e.g. a workflow step). */
  prefix?: string;
  required?: boolean;
  /** Shown on hover of the name (e.g. the field's description). */
  title?: string;
  /** Nothing chosen yet: the name reads as a placeholder. */
  muted?: boolean;
  /** The second line when the field has no type (e.g. "fixed value"). */
  note?: ReactNode;
}

/**
 * One side of a mapping pair, the same wherever fields are mapped (input
 * mapping, output mapping, auto-mapping): the name on the first line, the
 * type below it. Two lines, so both sides fit beside each other in a narrow
 * pane and a pair is recognised at a glance.
 */
export const MappingCell: FC<MappingCellProps> = ({
  name,
  type,
  prefix,
  required = false,
  title,
  muted = false,
  note,
}) => (
  <span className={styles.cell} data-streamlain-mapping-cell>
    <span
      className={styles.name}
      data-muted={muted || undefined}
      title={title ?? (typeof name === 'string' ? name : undefined)}
    >
      {prefix && (
        <span className={styles.prefix}>
          {prefix}
          {' › '}
        </span>
      )}
      {name}
      {required && (
        <span className={styles.required} aria-hidden="true">
          *
        </span>
      )}
    </span>
    {(type || note || !muted) && (
      <span className={styles.type}>
        {type ? <TypeChip type={type} small style={NO_CHIP_GAP} /> : note}
      </span>
    )}
  </span>
);
